// deviceHeadingBus — zentraler Broker für DeviceOrientationEvent-Heading.
//
// Warum ein Bus?
//   Bis Wave 14al hatten CompassRose und LocateControl JEDE einen eigenen
//   Plan, DeviceOrientationEvent zu abonnieren:
//     - CompassRose: nur im 'heading'-Mode — dreht die Map-Bearing nach
//       Handy-Kompass.
//     - LocateControl: gar nicht — das User-Dreieck rotiert bisher anhand
//       von GPS coords.heading, das aber NULL ist wenn der User steht.
//   Resultat (User-Bug Wave 14al): "wenn ich mich drehe, muss das dreieck
//   immer in die richtung gucken in die ich schaue. es bleibt aber fix."
//
//   Mit einem shared Bus:
//     1) Nur EIN window-Listener (statt zwei) — effizienter, keine Drift
//        zwischen zwei Subscribern die dasselbe Event etwas anders debouncen.
//     2) Permission ist zentral — wer zuerst Permission holt, profitieren
//        beide Subscriber. Kein doppelter iOS-Prompt.
//     3) LocateControl kann das User-Dreieck an der gleichen Quelle richten
//        wie der Map-Bearing — beide drehen sich synchron, das Dreieck zeigt
//        immer in die physische Blickrichtung.
//
// Semantik:
//   - subscribe(fn): fn wird bei jedem neuen Heading (in Grad, im Uhrzeiger-
//     sinn von Nord, 0..360) gecalled. Rückgabe ist die Unsubscribe-Funktion.
//   - Erster Subscriber hängt den window-Listener an, letzter Unsubscriber
//     entfernt ihn. Zwischen beiden bleibt der Listener konstant aktiv.
//   - latest(): synchroner Getter für den zuletzt empfangenen Wert, oder null
//     bevor das erste Event ankam.
//   - requestPermissionIfNeeded(): auf iOS 13+ muss das im User-Gesture-
//     Kontext gecalled werden. Einmal granted, bleibt granted für die
//     Session. Andere Plattformen: No-Op, returnt true.
//
// iOS-Eigenheiten:
//   - `DeviceOrientationEvent.requestPermission()` existiert nur auf iOS.
//   - Ohne Permission feuert deviceorientation einfach nicht — keine Events,
//     keine Exceptions. Daher kann ein "silent subscribe" sinnvoll sein:
//     Wenn der User später über den Kompass-Toggle Permission erteilt, kriegt
//     der silent Subscriber AUTOMATISCH Events, weil der window-Listener
//     schon hängt.
//   - `deviceorientationabsolute` ist zuverlässiger (true-north-referenced)
//     als `deviceorientation` (kann drift haben). Wir nehmen abs wenn da.
//   - iOS Safari unterstützt `webkitCompassHeading` (magnetische Nordaus-
//     richtung, 0..360) direkt — das hat Priorität, weil's schon im "0 = Norden,
//     gegen-Uhrzeiger-geht-nicht-höher"-Schema ist. Android liefert `alpha`,
//     das 0 bei Norden ist ABER gegen den Uhrzeigersinn wächst — wir
//     invertieren zu 360 - alpha.

type HeadingListener = (heading: number) => void;

let current: number | null = null;
let attached = false;
const subscribers = new Set<HeadingListener>();

/**
 * Permission-State-Cache. Auf iOS 13+ wird `requestPermission()` nach erstem
 * 'granted' nicht mehr nachgefragt (Safari cached es pro Session). Wir cachen
 * zusätzlich in-memory, damit wiederholte Calls nicht unnötig in Promise-
 * Land gehen.
 */
let permissionState: 'unknown' | 'granted' | 'denied' = 'unknown';

function onOrientation(e: DeviceOrientationEvent): void {
  const anyE = e as DeviceOrientationEvent & { webkitCompassHeading?: number };
  let heading: number | null = null;
  if (typeof anyE.webkitCompassHeading === 'number') {
    heading = anyE.webkitCompassHeading;
  } else if (e.alpha != null) {
    heading = 360 - e.alpha;
  }
  if (heading == null || Number.isNaN(heading)) return;

  // Normalisieren auf [0, 360). Manche Android-Geräte liefern 361..380 in
  // kurzen Bursts bei Magnet-Kalibrierung.
  if (heading < 0) heading += 360;
  if (heading >= 360) heading -= 360;

  current = heading;
  // Snapshot in ein Array, damit ein Subscriber in seinem Callback den
  // Set iterieren & evtl. unsubscriben kann ohne ConcurrentModification.
  for (const fn of Array.from(subscribers)) {
    fn(heading);
  }
}

function attachListener(): void {
  if (attached) return;
  const hasAbsolute = 'ondeviceorientationabsolute' in window;
  // Cast auf EventListener wegen TS-Event-Map-Overload-Strenge.
  window.addEventListener(
    hasAbsolute ? 'deviceorientationabsolute' : 'deviceorientation',
    onOrientation as EventListener,
  );
  attached = true;
}

function detachListener(): void {
  if (!attached) return;
  const hasAbsolute = 'ondeviceorientationabsolute' in window;
  window.removeEventListener(
    hasAbsolute ? 'deviceorientationabsolute' : 'deviceorientation',
    onOrientation as EventListener,
  );
  attached = false;
  // current NICHT auf null zurücksetzen — falls gleich wieder jemand
  // subscribed, soll er den letzten Wert sofort haben.
}

export const deviceHeadingBus = {
  /** Letztes empfangenes Heading (0..360) oder null wenn noch nie ein Event kam. */
  latest(): number | null {
    return current;
  },

  /**
   * Subscribe auf Heading-Updates.
   *
   * - Erster Subscriber attached den window-Listener.
   * - Letzter Unsubscriber detached ihn.
   * - Wenn bereits ein `current`-Wert vorliegt, wird der neue Subscriber
   *   NICHT synchron damit initialisiert (callbacks feuern erst beim
   *   nächsten Event) — der Subscriber kann `latest()` selbst abfragen
   *   wenn er den Initial-Wert will.
   */
  subscribe(fn: HeadingListener): () => void {
    subscribers.add(fn);
    attachListener();
    return () => {
      subscribers.delete(fn);
      if (subscribers.size === 0) detachListener();
    };
  },

  /**
   * iOS 13+: verlangt Permission für DeviceOrientationEvent. MUSS im User-
   * Gesture-Kontext aufgerufen werden (click/touchend Callback), sonst
   * rejected der Call silent.
   *
   * Andere Plattformen: permissionState wird direkt auf 'granted' gesetzt
   * und true zurückgegeben (kein nativer API-Call nötig).
   *
   * Idempotent: wiederholte Aufrufe nach 'granted' returnen sofort true.
   * Nach 'denied' returnen sie false — der User muss die Permission in
   * den iOS-Settings neu vergeben.
   */
  async requestPermissionIfNeeded(): Promise<boolean> {
    if (permissionState === 'granted') return true;
    if (permissionState === 'denied') return false;
    const AnyOrient = DeviceOrientationEvent as unknown as {
      requestPermission?: () => Promise<'granted' | 'denied'>;
    };
    if (typeof AnyOrient.requestPermission !== 'function') {
      // Nicht-iOS: keine Permission-Zeremonie. Listener wird beim subscribe
      // sofort Events liefern (sofern die Hardware den Sensor hat).
      permissionState = 'granted';
      return true;
    }
    try {
      const result = await AnyOrient.requestPermission();
      permissionState = result === 'granted' ? 'granted' : 'denied';
      return permissionState === 'granted';
    } catch (err) {
      // WICHTIG (Wave 14ap, User-Bug "heading-Mode geht nie an"):
      // catch wird primär getroffen wenn requestPermission() out-of-user-
      // gesture gerufen wird → iOS wirft NotAllowedError. Das ist NICHT
      // dasselbe wie "User hat Dialog weggeklickt" — der Dialog wurde gar
      // nicht gezeigt.
      //
      // Vorher: state = 'denied' gecacht → ALLE späteren Calls (auch aus
      // echten User-Gesten wie Rose-Tap) returnten sofort false, ohne den
      // iOS-Prompt überhaupt zu zeigen. Beobachtetes Symptom: Locate-Dreieck
      // rotiert (window-Listener hängt), aber Rose-Tap "macht nichts" /
      // unser Deny-Alert feuert, obwohl der User nie "Nein" gesagt hat.
      //
      // Fix: state bleibt 'unknown', damit der NÄCHSTE Call aus einem
      // echten User-Gesture-Kontext (Rose-Tap, Ride-Button) den Prompt
      // wirklich triggert. 'denied' cachen wir nur noch wenn die API
      // result: 'denied' liefert (echter Nein-Klick des Users).
      console.warn(
        '[deviceHeadingBus] requestPermission threw (likely out-of-gesture); keeping state=unknown',
        err,
      );
      return false;
    }
  },

  /** Nur für Tests / Reset-Scenarios. Produktion ruft das nicht. */
  _reset(): void {
    subscribers.clear();
    detachListener();
    current = null;
    permissionState = 'unknown';
  },
};
